import React, { useMemo, useState } from 'react';
import { Button, ConfigProvider, Input, message, Select, Table as AntTable, Typography } from 'antd';
import type { TableProps } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import Table from './TableResizable';
import ExportMenuButton from './Common/ExportMenuButton';
import { exporterTableau, FormatExport } from '../Functions/Traitement/ExportTableau';
import formatNumber from '../Functions/Traitement/FormatNumber';
import ReformatNumber from '../Functions/Traitement/ReformatNumber';

// Filtre déroulant construit à partir des valeurs présentes dans les données
export interface FiltreTableau {
  champ: string;
  label: string;
  champLib?: string; // colonne libellé associée (affichage "code - libellé")
}

interface Props<T> extends TableProps<T> {
  titreExport: string;
  nomFichier: string;
  filtres?: FiltreTableau[];
  // Valeurs pré-sélectionnées des filtres (clé = f.champ, ex. { Statut: 'EN_ATTENTE' }) —
  // « Réinitialiser » revient à ces valeurs au lieu de tout vider
  valeursFiltresDefaut?: Record<string, string>;
  // Colonne montant : placée juste après l'identification du dossier, mise en évidence, totalisée en pied
  champMontant?: string;
  // Autres colonnes totalisées en pied (ex. total décaissé, reste à décaisser)
  champsTotaux?: string[];
  // Libellé du compteur de lignes en pied (ex. "opération" → "15 opération(s)")
  libelleElement?: string;
  // Le tableau occupe toute la largeur du conteneur parent, sans son propre cadre
  // (à utiliser quand il est intégré dans une carte/section existante)
  pleineLargeur?: boolean;
}

const texteCellule = (v: unknown) => (v === null || v === undefined ? '' : String(v));

// Clés renvoyées par ExportMenuButton → formats de exporterTableau
const FORMATS_MENU: Record<string, FormatExport> = { pdf: 'pdf', xlsx: 'excel', docx: 'word', csv: 'csv' };

const SANS_TOTAUX: string[] = [];
const LARGEUR_COLONNE_DEFAUT = 150;
const LARGEUR_SELECTION = 48;
const LARGEUR_MONTANT = 170;
// Colonnes d'identification après lesquelles le montant est affiché
const CHAMPS_IDENTIFICATION = ['NumeroDemande', 'NumeroFinancement', 'NUMCLI', 'Numcli', 'NOMCLI', 'Nomcli', 'lib_NUMCLI', 'lib_NOMCLI'];

// ==================== TABLEAU AVEC RECHERCHE / FILTRES / EXPORT ====================
// Même barre d'outils que l'écran d'audit, filtrage côté client sur les lignes chargées
function TableauDossiers<T extends object = any>({ titreExport, nomFichier, filtres = [], valeursFiltresDefaut, champMontant, champsTotaux = SANS_TOTAUX, libelleElement = 'dossier', pleineLargeur = false, dataSource, columns, scroll, ...rest }: Props<T>) {
  const [recherche, setRecherche] = useState('');
  const [valeursFiltres, setValeursFiltres] = useState<Record<string, string | undefined>>({ ...valeursFiltresDefaut });

  const lignes = useMemo(() => (Array.isArray(dataSource) ? [...dataSource] : []), [dataSource]);

  const optionsFiltres = useMemo(
    () =>
      filtres.map((f) => {
        const vues = new Map<string, string>();
        lignes.forEach((r: any) => {
          const code = texteCellule(r[f.champ]);
          if (code && !vues.has(code)) vues.set(code, f.champLib && r[f.champLib] ? `${code} - ${r[f.champLib]}` : code);
        });
        return { ...f, options: Array.from(vues, ([value, label]) => ({ value, label })) };
      }),
    [filtres, lignes]
  );

  const lignesFiltrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return lignes.filter((r: any) => {
      if (filtres.some((f) => valeursFiltres[f.champ] && texteCellule(r[f.champ]) !== valeursFiltres[f.champ])) return false;
      return !terme || Object.values(r).some((v) => texteCellule(v).toLowerCase().includes(terme));
    });
  }, [lignes, recherche, valeursFiltres, filtres]);

  // Une ligne par enregistrement : largeur fixe + texte tronqué (info-bulle) sur chaque colonne
  const colonnes = useMemo(() => {
    const totalises = [champMontant, ...champsTotaux].filter(Boolean);
    const cols: any[] = (columns || []).map((c: any) => {
      const col = { ellipsis: { showTitle: true }, ...c, width: c.width ?? LARGEUR_COLONNE_DEFAUT };
      if (!totalises.includes(c.dataIndex)) return col;
      // Colonne montant : jamais "NaN" (valeur déjà formatée, vide ou texte) → relecture via ReformatNumber
      return {
        ...col,
        render: (v: any, r: any, i: number) => {
          const rendu = c.render ? c.render(v, r, i) : v;
          return rendu === undefined || rendu === null || String(rendu).includes('NaN') ? formatNumber(ReformatNumber(v)) : rendu;
        },
      };
    });
    const iMontant = champMontant ? cols.findIndex((c) => c.dataIndex === champMontant) : -1;
    if (iMontant < 0) return cols;
    const [montant] = cols.splice(iMontant, 1);
    const iIdent = Math.max(-1, ...CHAMPS_IDENTIFICATION.map((k) => cols.findIndex((c) => c.dataIndex === k)));
    cols.splice(iIdent + 1, 0, {
      ...montant,
      width: Math.max(Number(montant.width) || 0, LARGEUR_MONTANT),
      align: 'right',
      className: ['colonneMontant', montant.className].filter(Boolean).join(' '),
    });
    return cols;
  }, [columns, champMontant, champsTotaux]);
  const largeurTotale =
    colonnes.reduce((t: number, c: any) => t + (Number(c.width) || LARGEUR_COLONNE_DEFAUT), 0) + (rest.rowSelection ? LARGEUR_SELECTION : 0);

  // Pied : nombre de dossiers + chaque total placé exactement sous sa colonne
  const champsTotalises = [champMontant, ...champsTotaux].filter(Boolean) as string[];
  const piedTotaux = (pageData: readonly T[]) => {
    const rows = Array.isArray(pageData) ? pageData : [];
    const positions = colonnes.map((c: any, i: number) => (champsTotalises.includes(c.dataIndex) ? i : -1)).filter((i: number) => i >= 0);
    if (rows.length === 0 || positions.length === 0) return null;
    const decalage = rest.rowSelection ? 1 : 0;
    // ReformatNumber : accepte un nombre brut ou un montant déjà formaté ("1 250 000,00")
    const total = (champ: string) => rows.reduce((t: number, r: any) => t + ReformatNumber(r[champ]), 0);
    const cellules: React.ReactNode[] = [
      <AntTable.Summary.Cell key="libelle" index={0} colSpan={positions[0] + decalage}>{`${rows.length} ${libelleElement}(s) — Total`}</AntTable.Summary.Cell>,
    ];
    for (let i = positions[0]; i < colonnes.length; i++) {
      const c: any = colonnes[i];
      cellules.push(
        champsTotalises.includes(c.dataIndex) ? (
          <AntTable.Summary.Cell key={i} index={i + decalage} align="right" className={c.dataIndex === champMontant ? 'colonneMontant' : undefined}>
            {formatNumber(total(c.dataIndex))}
          </AntTable.Summary.Cell>
        ) : (
          <AntTable.Summary.Cell key={i} index={i + decalage} />
        )
      );
    }
    return (
      <AntTable.Summary fixed>
        <AntTable.Summary.Row style={{ fontWeight: 700, backgroundColor: '#e6f4ff' }}>{cellules}</AntTable.Summary.Row>
      </AntTable.Summary>
    );
  };

  function reinitialiser() {
    setRecherche('');
    setValeursFiltres({ ...valeursFiltresDefaut });
  }

  // Exporte les lignes filtrées avec les en-têtes et le rendu texte des colonnes affichées
  function exporter(format: FormatExport) {
    if (lignesFiltrees.length === 0) {
      message.info('Aucune donnée à exporter.');
      return;
    }
    const cols = (columns || []).filter((c: any) => c.dataIndex !== undefined && typeof c.title === 'string');
    const export_ = lignesFiltrees.map((r: any, i) => {
      const ligne: Record<string, unknown> = {};
      cols.forEach((c: any) => {
        const brut = r[c.dataIndex];
        const rendu = c.render ? c.render(brut, r, i) : brut;
        ligne[c.title] = typeof rendu === 'string' || typeof rendu === 'number' ? rendu : texteCellule(brut);
      });
      return ligne;
    });
    exporterTableau(export_, format, titreExport, nomFichier);
  }

  return (
    <div className="zoneTableauExterne">
    <div className={pleineLargeur ? 'zoneTableau zoneTableauPleine' : 'zoneTableau'}>
      {/* Barre d'outils toujours active, même si le tableau est dans un <Form disabled> */}
      <ConfigProvider componentDisabled={false}>
      <div className="barreOutilsTableau">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Rechercher (n° demande, client, montant…)"
          className="rechercheTableau"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        {optionsFiltres.map((f) => (
          <Select
            key={f.champ}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={f.label}
            className="filtreTableau"
            value={valeursFiltres[f.champ]}
            options={f.options}
            onChange={(v) => setValeursFiltres((p) => ({ ...p, [f.champ]: v }))}
          />
        ))}
        <Button icon={<ReloadOutlined />} onClick={reinitialiser}>Réinitialiser</Button>
        {/* Extrémité droite : compteur + bouton d'export unique (menu déroulant des formats) */}
        <div className="actionsDroiteTableau">
          <Typography.Text type="secondary">
            {lignesFiltrees.length === lignes.length ? `${lignes.length} ligne(s)` : `${lignesFiltrees.length} / ${lignes.length} ligne(s)`}
          </Typography.Text>
          <ExportMenuButton size="middle" disabled={lignesFiltrees.length === 0} onExport={(cle) => exporter(FORMATS_MENU[cle] ?? 'excel')} />
        </div>
      </div>
      </ConfigProvider>
      <Table
        bordered
        tableLayout="fixed"
        summary={champsTotalises.length ? piedTotaux : undefined}
        {...rest}
        style={{ width: '100%', ...rest.style }}
        columns={colonnes}
        dataSource={lignesFiltrees as T[]}
        scroll={{ ...scroll, x: Math.max(Number(scroll?.x) || 0, largeurTotale) }}
      />
    </div>
    </div>
  );
}

export default TableauDossiers;
